"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { sendEvent } from "@/lib/utils/analytics";
import { DEFAULT_SCROLL_PERCENTAGE_THRESHOLDS } from "@/components/analytics/scroll-percentage-thresholds";

export function PageScrollEvents({ eventName }: { eventName: string }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const triggeredRef = useRef<Set<number>>(new Set());
  const tickingRef = useRef(false);

  useEffect(() => {
    triggeredRef.current = new Set();
  }, [pathname, searchParams, eventName]);

  useEffect(() => {
    const thresholds = DEFAULT_SCROLL_PERCENTAGE_THRESHOLDS;

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;

      requestAnimationFrame(() => {
        const docEl = document.documentElement;
        const scrollTop = window.scrollY || docEl.scrollTop || 0;
        const docHeight = docEl.scrollHeight - window.innerHeight;
        if (docHeight > 0) {
          const percent = (scrollTop / docHeight) * 100;
          for (const t of thresholds) {
            if (percent >= t && !triggeredRef.current.has(t)) {
              sendEvent(eventName, {
                percentage_scrolled: t,
              });
              triggeredRef.current.add(t);
            }
          }
        }
        tickingRef.current = false;
      });
    };

    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [eventName]);

  return null;
}
